/**
 * Touch-and-hold, which is what a right-click is on a phone.
 *
 * The bench's menus — put a burner under this, run a delivery tube from it,
 * reshape it, take it away — hung entirely off `contextmenu`, an event a
 * finger has no way to send. This is the same menu, opened by holding.
 *
 * Why hold and not double-tap: hold is the platform convention for "show me
 * what I can do with this" on both iOS and Android, so it is the first thing
 * anyone tries. Double-tap is spoken for — the browser zooms with it — and
 * where apps do use it it means a quick action, never a menu.
 *
 * The details are what make it feel native rather than accidental:
 *
 * - 450ms: a touch under the 500ms iOS's own touch-and-hold recogniser waits
 *   for, so ours has fired before the system's could claim the touch.
 * - Timed from TOUCH events, not pointer events, on a finger. iOS Safari sends
 *   `pointercancel` to a held finger as its own gestures start to look at the
 *   touch — which cancelled the hold before it fired, so on an iPhone holding
 *   never opened anything. Touch events keep arriving for the same finger
 *   (touchmove still tells a scroll from a hold), and are what iOS has always
 *   delivered reliably. Pointer events still time a pen.
 * - Cancelled by movement past a few pixels, because the same gesture starts a
 *   scroll and starts dragging one of the blocks beside a vessel, and by a
 *   lift before the time is up, which is a tap.
 * - A short buzz the moment it fires, where the device has one. The menu opens
 *   under a finger that is still down; without the buzz it reads as something
 *   the page did by itself.
 * - Mouse pointers are left alone: they have a right button, and holding one
 *   down is how you drag.
 * - `contextmenu` is still handled, and cancels any pending hold. Android
 *   Chrome raises it from the long-press itself, so without that both would
 *   run and the menu would open twice.
 *
 * The caller must also keep the platform's own long-press behaviour off the
 * element — `-webkit-touch-callout: none` and `user-select: none`, or iOS puts
 * its Copy/Share sheet over ours.
 */
import { useCallback, useEffect, useRef } from 'react';

const HOLD_MS = 450;
/** How far a finger may wander and still be holding rather than dragging. */
const SLOP_PX = 10;

type PointerHandler = (e: React.PointerEvent) => void;
type TouchHandler = (e: React.TouchEvent) => void;

export interface LongPressHandlers {
  onContextMenu: (e: React.MouseEvent) => void;
  onPointerDown: PointerHandler;
  onPointerMove: PointerHandler;
  onPointerUp: PointerHandler;
  onPointerCancel: PointerHandler;
  onTouchStart: TouchHandler;
  onTouchMove: TouchHandler;
  onTouchEnd: TouchHandler;
  onTouchCancel: TouchHandler;
}

export function useLongPress(open: (clientX: number, clientY: number) => void): LongPressHandlers {
  // The callback is rebuilt on every render of the bench; the timer that fires
  // it may have been set two renders ago.
  const openRef = useRef(open);
  openRef.current = open;

  const timer = useRef<number | null>(null);
  const from = useRef<{ x: number; y: number } | null>(null);
  /** The hold opened the menu, so the lift that ends it is not a tap. */
  const fired = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    from.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const start = (x: number, y: number): void => {
    cancel();
    from.current = { x, y };
    fired.current = false;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      from.current = null;
      fired.current = true;
      navigator.vibrate?.(10);
      openRef.current(x, y);
    }, HOLD_MS);
  };
  const moved = (x: number, y: number): void => {
    const at = from.current;
    if (!at) return;
    if (Math.abs(x - at.x) > SLOP_PX || Math.abs(y - at.y) > SLOP_PX) cancel();
  };

  return {
    onContextMenu: (e) => {
      e.preventDefault();
      e.stopPropagation();
      cancel();
      openRef.current(e.clientX, e.clientY);
    },
    // A pen only: a mouse has a right button, and a finger is timed below.
    onPointerDown: (e) => { if (e.pointerType === 'pen') start(e.clientX, e.clientY); },
    onPointerMove: (e) => { if (e.pointerType === 'pen') moved(e.clientX, e.clientY); },
    onPointerUp: (e) => { if (e.pointerType === 'pen') cancel(); },
    onPointerCancel: (e) => { if (e.pointerType === 'pen') cancel(); },
    onTouchStart: (e) => {
      // A second finger is a pinch, not a hold.
      if (e.touches.length !== 1) { cancel(); return; }
      const t = e.touches[0];
      start(t.clientX, t.clientY);
    },
    onTouchMove: (e) => {
      const t = e.touches[0];
      if (t) moved(t.clientX, t.clientY);
    },
    // The finger lifting off a menu it just opened would otherwise land as a
    // click on the vessel under it; preventing the touchend stops that click.
    onTouchEnd: (e) => {
      if (fired.current) { fired.current = false; e.preventDefault(); }
      cancel();
    },
    onTouchCancel: cancel,
  };
}
